// services/SegmentsApiService.js - FIXED VERSION to prevent refresh loops

import { getEnv } from "../config/env";
import { getRecommendedThreshold } from "../utils";

// Terms reach this service with `score` already converted to a cosine
// distance. Bare strings fall back to the sheet default (Strong Match).
const SEGMENT_DEFAULT_THRESHOLD = getRecommendedThreshold("SEGMENTS");

class SegmentsApiService {
	constructor() {
		const env = getEnv();
		this.baseURL = env.REACT_APP_API_URL;
		this.proxyUrl = env.REACT_APP_PROXY;
		this.timeout = 300000;
		this.filtersCache = new Map();
		this.filtersCacheTimeout = 10 * 60 * 1000;

		// ✅ CRITICAL: Add redirect prevention
		this.isRedirecting = false;
		this.lastRedirectTime = 0;
		this.REDIRECT_COOLDOWN = 5000; // 5 seconds cooldown

		console.log(
			"🔧 FIXED SegmentsApiService loaded with redirect loop prevention",
		);
	}

	getSharedAuthToken() {
		if (
			window.apiService &&
			window.apiService.tokens &&
			window.apiService.tokens.idToken
		) {
			return window.apiService.tokens.idToken;
		}
		return (
			localStorage.getItem("google_id_token") ||
			localStorage.getItem("authToken")
		);
	}

	setProxyUrl(url) {
		this.proxyUrl = url.replace(/\/$/, "");
	}

	// ✅ CRITICAL: Fixed authentication check with redirect prevention
	isAuthenticated() {
		// Skip auth check if we're already redirecting
		if (this.isRedirecting) {
			console.log("🔄 Skipping auth check - redirect in progress");
			return false;
		}

		if (
			window.apiService &&
			typeof window.apiService.isAuthenticated === "function"
		) {
			try {
				const mainServiceAuth = window.apiService.isAuthenticated();
				if (!mainServiceAuth) {
					console.log(
						"⚠️ Main service auth failed, but not redirecting from segments service",
					);
					return false; // ✅ Don't redirect here, let main service handle it
				}
				return mainServiceAuth;
			} catch (error) {
				console.warn("Main ApiService auth check failed:", error);
				return false; // ✅ Don't redirect on error
			}
		}

		const token = this.getSharedAuthToken();
		if (!token) {
			console.log(
				"⚠️ No token found, but not redirecting from segments service",
			);
			return false; // ✅ Don't redirect here
		}

		return this.checkTokenExpiration(token);
	}

	// ✅ CRITICAL: Fixed redirect with cooldown prevention
	redirectToLogin() {
		// Prevent multiple redirects in short succession
		const now = Date.now();
		if (
			this.isRedirecting ||
			now - this.lastRedirectTime < this.REDIRECT_COOLDOWN
		) {
			console.log("🚫 Redirect prevented - cooldown active");
			return;
		}

		this.isRedirecting = true;
		this.lastRedirectTime = now;

		console.log("🔐 Segments API: Initiating single redirect to auth/login...");
		this.clearExpiredTokens();

		// ✅ Only redirect if we're not already on login page
		if (
			typeof window !== "undefined" &&
			window.location.pathname !== "/auth/login"
		) {
			// Use a timeout to prevent immediate loops
			setTimeout(() => {
				if (!this.isRedirecting) return; // Check if redirect was cancelled

				try {
					window.location.href = "/auth/login";
				} catch (error) {
					console.error("Redirect failed:", error);
					this.isRedirecting = false;
				}
			}, 100);
		} else {
			this.isRedirecting = false;
		}
	}

	clearExpiredTokens() {
		try {
			localStorage.removeItem("google_id_token");
			localStorage.removeItem("authToken");
			localStorage.removeItem("refresh_token");
			console.log("Segments API: Expired tokens cleared from storage");
		} catch (error) {
			console.warn("Failed to clear tokens:", error);
		}
	}

	// ✅ CRITICAL: Fixed token expiration check
	checkTokenExpiration(token) {
		if (!token) return false;

		try {
			const payload = this.parseJWT(token);
			if (!payload || !payload.exp) return false;

			const now = Date.now();
			const expiration = payload.exp * 1000;
			const isExpired = expiration <= now;

			if (isExpired) {
				console.log(
					"⚠️ Token expired, but not redirecting from segments service",
				);
				// ✅ Don't redirect here - let the main app handle it
				return false;
			}

			return true;
		} catch (error) {
			console.warn("Token validation failed:", error);
			return false; // ✅ Don't redirect on validation error
		}
	}

	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	getUser() {
		// ✅ Don't redirect if not authenticated, just return null
		const token = this.getSharedAuthToken();
		if (!token) return null;

		if (window.apiService && typeof window.apiService.getUser === "function") {
			try {
				return window.apiService.getUser();
			} catch (error) {
				console.warn("Main ApiService getUser failed:", error);
			}
		}

		try {
			const payload = this.parseJWT(token);
			return payload
				? {
						id: payload.sub,
						email: payload.email,
						name: payload.name,
						picture: payload.picture,
						email_verified: payload.email_verified,
					}
				: null;
		} catch {
			return null;
		}
	}

	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		const token = this.getSharedAuthToken();
		if (token && this.checkTokenExpiration(token)) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	// FIXED: Use proxy URL only
	async makeRequest(endpoint, options = {}) {
		try {
			const token = this.getSharedAuthToken();
			if (!token || this.isRedirecting) {
				return {
					success: false,
					error: "Authentication required",
					status: 401,
					skipRetry: true,
				};
			}

			const headers = this.getHeaders();
			const normalizedEndpoint = endpoint.startsWith("/")
				? endpoint
				: `/${endpoint}`;
			const url = `${this.proxyUrl}${normalizedEndpoint}`;

			const requestOptions = {
				method: "POST",
				...options,
				headers: { ...headers, ...options.headers },
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
			};

			console.log(`API Request (PROXY ONLY): ${requestOptions.method} ${url}`);

			const response = await fetch(url, {
				...requestOptions,
				signal: AbortSignal.timeout(this.timeout),
			});

			console.log(`API Response: ${response.status} ${response.statusText}`);

			if (response.status === 401 || response.status === 403) {
				console.log(
					"API authentication failed, but not redirecting from segments service",
				);
				return {
					success: false,
					error: "Authentication failed",
					status: response.status,
					skipRetry: true,
				};
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				console.error("API Error Response:", errorText);
				throw new Error(
					`HTTP ${response.status}: ${response.statusText}. ${errorText}`,
				);
			}

			const data = await response.json();
			console.log("API Response received successfully");

			return {
				success: true,
				data: data,
				status: response.status,
				headers: response.headers,
			};
		} catch (error) {
			console.error("API Request Error:", error);

			let errorMessage = error.message;
			if (
				error.name === "TypeError" &&
				error.message.includes("Failed to fetch")
			) {
				errorMessage = "Network error: Unable to connect to API";
			}

			return {
				success: false,
				error: errorMessage,
				status: error.status || 500,
				skipRetry: true,
			};
		}
	}

	async calculateOrfForSegments(orfRequest, signal = null) {
		const requestId = Date.now();
		try {
			console.log(`[${requestId}] Calculating ORF for segments:`, orfRequest);

			if (!orfRequest?.segmentIds || !Array.isArray(orfRequest.segmentIds)) {
				throw new Error("segmentIds array is required for ORF calculation");
			}

			if (orfRequest.segmentIds.length === 0) {
				throw new Error(
					"At least one segment ID is required for ORF calculation",
				);
			}

			// Check authentication
			const token = this.getSharedAuthToken();
			if (!token || this.isRedirecting) {
				return {
					success: false,
					error: "Authentication required",
					status: 401,
				};
			}

			// Get segment reach data from the request
			const children = orfRequest.segmentIds.map((segmentId) => ({
				id: segmentId,
				reach: orfRequest.segmentReachMap?.[segmentId] || 1000000, // Default reach if not provided
			}));

			// Prepare the ORF payload with correct structure
			const orfPayload = {
				expr: {
					op: orfRequest.operator || "UNION",
					children: children,
				},
			};

			console.log(
				`[${requestId}] ORF API Request payload:`,
				JSON.stringify(orfPayload, null, 2),
			);

			// Make direct fetch call to ORF endpoint
			const url = `${this.proxyUrl}/segment/calculate-orf`;
			const headers = this.getHeaders();

			const response = await fetch(url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(orfPayload),
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
				signal: signal || AbortSignal.timeout(this.timeout),
			});

			console.log(`[${requestId}] ORF API Response status: ${response.status}`);

			if (response.status === 401 || response.status === 403) {
				console.log("ORF API authentication failed");
				return {
					success: false,
					error: "Authentication failed",
					status: response.status,
				};
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				console.error("ORF API Error Response:", errorText);
				throw new Error(
					`HTTP ${response.status}: ${response.statusText}. ${errorText}`,
				);
			}

			const data = await response.json();
			console.log(`[${requestId}] ORF calculation successful:`, {
				orf: data?.orf,
				effectiveReach: data?.effectiveReach,
				segmentCount: orfRequest.segmentIds.length,
			});

			return {
				success: true,
				data: data,
				status: response.status,
			};
		} catch (error) {
			console.error(`[${requestId}] calculateOrfForSegments failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	// ✅ NEW: Fetch audience data for a specific segment
	async getSegmentAudienceData(segmentId, signal = null) {
		const requestId = Date.now();
		try {
			console.log(
				`🎯 [${requestId}] Fetching audience data for segment: ${segmentId}`,
			);

			if (!segmentId) {
				throw new Error("Segment ID is required");
			}

			const response = await this.makeRequest(`/audience/${segmentId}`, {
				method: "GET",
				signal,
			});

			if (response.success) {
				console.log(`✅ [${requestId}] Audience data fetched successfully:`, {
					segmentId,
					dataSize: response.data ? Object.keys(response.data).length : 0,
				});
				return response;
			} else {
				console.error(
					`❌ [${requestId}] Failed to fetch audience data:`,
					response.error,
				);
				return response;
			}
		} catch (error) {
			console.error(`❌ [${requestId}] getSegmentAudienceData failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	// ✅ CRITICAL: Fixed getFilterOptions with better error handling
	async getFilterOptions(signal = null) {
		try {
			console.log("🔧 Fetching filter options from API endpoint...");

			const user = this.getUser();
			const cacheKey = user
				? `segment_filter_options_${user.id}`
				: "segment_filter_options_anonymous";

			// Check cache first
			const cached = this.filtersCache.get(cacheKey);
			if (cached && Date.now() - cached.timestamp < this.filtersCacheTimeout) {
				console.log("✅ Using cached filter options");
				return {
					success: true,
					data: cached.data,
					cached: true,
				};
			}

			// ✅ Simple auth check without redirect
			const token = this.getSharedAuthToken();
			if (!token || this.isRedirecting) {
				console.log("⚠️ No authentication for filter options, returning error");
				return {
					success: false,
					error: "Authentication required for filter options",
					status: 401,
				};
			}

			try {
				console.log("🌐 Calling filter values API: /segment/get-filter-values");

				const response = await this.makeRequest("/segment/get-filter-values", {
					method: "GET",
				});

				if (response.success && response.data) {
					console.log("✅ Filter options fetched successfully from API");

					// ✅ CRITICAL FIX: Extract the segmentFilterValues from the response
					const filterData = response.data.segmentFilterValues || response.data;

					// Cache the successful response
					this.filtersCache.set(cacheKey, {
						data: filterData, // ✅ Store the actual filter values, not the wrapper
						timestamp: Date.now(),
					});

					return {
						success: true,
						data: filterData, // ✅ Return the actual filter values
						cached: false,
					};
				} else if (response.skipRetry) {
					// ✅ Don't retry on auth errors
					return {
						success: false,
						error: response.error || "Authentication failed",
						status: response.status || 401,
					};
				} else {
					throw new Error(response.error || "Failed to fetch filter options");
				}
			} catch (apiError) {
				console.warn(
					"⚠️ Failed to fetch filter options from API:",
					apiError.message,
				);
				return {
					success: false,
					error: apiError.message || "Failed to fetch filter options from API",
				};
			}
		} catch (error) {
			console.error("❌ Get filter options failed completely:", error);
			return {
				success: false,
				error: error.message || "Filter options request failed",
			};
		}
	}

	// Helper function to safely preserve array values
	preserveArrayValue(value, fieldName) {
		if (!value) {
			console.log(
				`🔍 CORRECTED: ${fieldName} is null/undefined, returning empty array`,
			);
			return [];
		}

		if (Array.isArray(value)) {
			console.log(
				`🔍 CORRECTED: ${fieldName} is valid array with ${value.length} items:`,
				value,
			);
			return [...value]; // Create new array to prevent mutations
		}

		console.warn(
			`⚠️ CORRECTED: ${fieldName} expected array but got ${typeof value}:`,
			value,
		);
		return [];
	}

	// Helper function to safely preserve numeric values
	preserveNumericValue(value, fieldName, defaultValue = 0) {
		if (value === null || value === undefined) {
			console.log(
				`🔍 CORRECTED: ${fieldName} is null/undefined, using default: ${defaultValue}`,
			);
			return defaultValue;
		}

		if (typeof value === "number") {
			console.log(`🔍 CORRECTED: ${fieldName} is valid number: ${value}`);
			return value;
		}

		const parsed = parseFloat(value);
		if (!isNaN(parsed)) {
			console.log(
				`🔍 CORRECTED: ${fieldName} parsed from ${typeof value} to number: ${parsed}`,
			);
			return parsed;
		}

		console.warn(
			`⚠️ CORRECTED: ${fieldName} could not be parsed as number, using default:`,
			{
				value,
				type: typeof value,
				defaultValue,
			},
		);
		return defaultValue;
	}

	async performSegmentSearch(
		searchTerms = [],
		excludeTerms = [],
		filters = {},
		options = {},
	) {
		const requestId = Date.now();
		try {
			console.log(`🔧 CORRECTED performSegmentSearch called [${requestId}]:`);
			console.log("  🔥 searchTerms param:", searchTerms);
			console.log("  🔥 excludeTerms param:", excludeTerms);
			console.log("  🔥 options:", options);
			console.log("  🔥 filters received:", {
				filterKeys: Object.keys(filters),
				segmentVectorKeys: filters.segmentVector
					? Object.keys(filters.segmentVector)
					: [],
				segmentMetricsKeys: filters.segmentMetrics
					? Object.keys(filters.segmentMetrics)
					: [],
				hasValidStructure: !!(filters.segmentVector && filters.segmentMetrics),
			});

			// Handle search terms - prioritize options over parameters
			let finalSearchQueries = [];
			let finalExcludeQueries = [];

			// Handle search terms from options (component) - prioritize these
			if (options.searchTerms && Array.isArray(options.searchTerms)) {
				finalSearchQueries = options.searchTerms
					.map((term) => {
						if (typeof term === "object" && term.text) {
							console.log(
								`✅ Processing search term: "${term.text}" with score: ${term.score}`,
							);
							return {
								query: term.text,
								threshold: term.score ?? SEGMENT_DEFAULT_THRESHOLD,
							};
						} else if (typeof term === "string") {
							console.log(
								`🔍 Processing search term (string): "${term}" with the default segment threshold`,
							);
							return {
								query: term,
								threshold: SEGMENT_DEFAULT_THRESHOLD,
							};
						}
						return null;
					})
					.filter(Boolean);

				console.log("✅ Final search queries with scores:", finalSearchQueries);
			} else if (Array.isArray(searchTerms) && searchTerms.length > 0) {
				finalSearchQueries = searchTerms
					.map((term) => {
						if (typeof term === "object" && term.text) {
							return {
								query: term.text,
								threshold: term.score ?? SEGMENT_DEFAULT_THRESHOLD,
							};
						} else if (typeof term === "string") {
							return {
								query: term,
								threshold: SEGMENT_DEFAULT_THRESHOLD,
							};
						}
						return null;
					})
					.filter(Boolean);

				console.log("🔍 Search queries from parameters:", finalSearchQueries);
			}

			// Handle exclude terms
			if (options.excludeTerms && Array.isArray(options.excludeTerms)) {
				finalExcludeQueries = options.excludeTerms
					.map((term) => {
						if (typeof term === "object" && term.text) {
							console.log(
								`❌ Processing exclude term: "${term.text}" with score: ${term.score}`,
							);
							return {
								query: term.text,
								threshold: term.score ?? SEGMENT_DEFAULT_THRESHOLD,
							};
						} else if (typeof term === "string") {
							console.log(
								`🔍 Processing exclude term (string): "${term}" with the default segment threshold`,
							);
							return {
								query: term,
								threshold: SEGMENT_DEFAULT_THRESHOLD,
							};
						}
						return null;
					})
					.filter(Boolean);
			} else if (Array.isArray(excludeTerms) && excludeTerms.length > 0) {
				finalExcludeQueries = excludeTerms
					.map((term) => {
						if (typeof term === "object" && term.text) {
							return {
								query: term.text,
								threshold: term.score ?? SEGMENT_DEFAULT_THRESHOLD,
							};
						} else if (typeof term === "string") {
							return {
								query: term,
								threshold: SEGMENT_DEFAULT_THRESHOLD,
							};
						}
						return null;
					})
					.filter(Boolean);
			}

			// Improved filter preservation with validation
			const workingFilters = JSON.parse(JSON.stringify(filters)); // Deep copy to avoid mutations

			// Only remove search-related fields, preserve all filter values
			const searchRelatedFields = [
				"searchQuery",
				"searchTerms",
				"excludeTerms",
			];
			const removedFields = [];

			searchRelatedFields.forEach((field) => {
				if (workingFilters[field] !== undefined) {
					delete workingFilters[field];
					removedFields.push(field);
				}
			});

			if (removedFields.length > 0) {
				console.log(
					`🧹 CORRECTED [${requestId}]: Removed only search-related fields:`,
					removedFields,
				);
			}

			console.log(
				`🔧 CORRECTED [${requestId}]: Working filters after cleanup:`,
				{
					workingFilterKeys: Object.keys(workingFilters),
					hasSegmentVector: !!workingFilters.segmentVector,
					hasSegmentMetrics: !!workingFilters.segmentMetrics,
					preservedAudienceType: workingFilters.segmentVector?.audienceType,
					preservedCategoryL1: workingFilters.segmentVector?.categoryL1,
					preservedSource: workingFilters.segmentVector?.source,
					preservedReach: workingFilters.segmentMetrics?.reach,
					preservedAvgEcpm: workingFilters.segmentMetrics?.avgEcpm,
				},
			);

			const processedCategoryFilters = Array.isArray(
				workingFilters.categoryFilters,
			)
				? workingFilters.categoryFilters.filter((filter) => filter.categoryL2) // Only include filters with selected categories
				: [];

			console.log(
				`🔧 CORRECTED [${requestId}]: Processed category filters:`,
				processedCategoryFilters,
			);

			// Build payload with bulletproof filter preservation
			const searchPayload = {
				query: finalSearchQueries,
				excludeQuery: finalExcludeQueries,
				filters: {
					segmentVector: {
						// Preserve string/null fields safely
						originalSegmentPath:
							workingFilters.segmentVector?.originalSegmentPath || null,
						dataProvider: workingFilters.segmentVector?.dataProvider || null,
						adHocInfo: workingFilters.segmentVector?.adHocInfo || null,
						fullName: workingFilters.segmentVector?.fullName || null,
						segmentSourceDescription:
							workingFilters.segmentVector?.segmentSourceDescription || null,
						segmentName: workingFilters.segmentVector?.segmentName || null,
						description: workingFilters.segmentVector?.description || null,
						status: null,
						propensity: workingFilters.segmentVector?.propensity || null,
						seedAggregateSegmentId:
							workingFilters.segmentVector?.seedAggregateSegmentId || null,
						lookalikeDefinitionId:
							workingFilters.segmentVector?.lookalikeDefinitionId || null,
						gcsFilePath: workingFilters.segmentVector?.gcsFilePath || null,

						// Use helper functions for bulletproof array preservation
						audienceType: this.preserveArrayValue(
							workingFilters.segmentVector?.audienceType,
							"audienceType",
						),
						categoryL1: this.preserveArrayValue(
							workingFilters.segmentVector?.categoryL1,
							"categoryL1",
						),
						categoryL2: this.preserveArrayValue(
							workingFilters.segmentVector?.categoryL2,
							"categoryL2",
						),
						attributeName: this.preserveArrayValue(
							workingFilters.segmentVector?.attributeName,
							"attributeName",
						),
						attributeValue: this.preserveArrayValue(
							workingFilters.segmentVector?.attributeValue,
							"attributeValue",
						),
						valueQualifier: this.preserveArrayValue(
							workingFilters.segmentVector?.valueQualifier,
							"valueQualifier",
						),
						accountName: this.preserveArrayValue(
							workingFilters.segmentVector?.accountName,
							"accountName",
						),
						bqLocation: this.preserveArrayValue(
							workingFilters.segmentVector?.bqLocation,
							"bqLocation",
						),
						externalId: this.preserveArrayValue(
							workingFilters.segmentVector?.externalId,
							"externalId",
						),
						type: this.preserveArrayValue(
							workingFilters.segmentVector?.type,
							"type",
						),
						subType: this.preserveArrayValue(
							workingFilters.segmentVector?.subType,
							"subType",
						),
						segmentGroupId: this.preserveArrayValue(
							workingFilters.segmentVector?.segmentGroupId,
							"segmentGroupId",
						),
						category: this.preserveArrayValue(
							workingFilters.segmentVector?.category,
							"category",
						),
						subCategory: this.preserveArrayValue(
							workingFilters.segmentVector?.subCategory,
							"subCategory",
						),
						source: this.preserveArrayValue(
							workingFilters.segmentVector?.source,
							"source",
						),
						providerId: this.preserveArrayValue(
							workingFilters.segmentVector?.providerId,
							"providerId",
						),
						segmentCategory: this.preserveArrayValue(
							workingFilters.segmentVector?.segmentCategory,
							"segmentCategory",
						),
						segmentType: this.preserveArrayValue(
							workingFilters.segmentVector?.segmentType,
							"segmentType",
						),
						segmentSource: this.preserveArrayValue(
							workingFilters.segmentVector?.segmentSource,
							"segmentSource",
						),
						allowedAccounts: this.preserveArrayValue(
							workingFilters.segmentVector?.allowedAccounts,
							"allowedAccounts",
						),
						metadata: this.preserveArrayValue(
							workingFilters.segmentVector?.metadata,
							"metadata",
						),
						sourceDetailedCounts: this.preserveArrayValue(
							workingFilters.segmentVector?.sourceDetailedCounts,
							"sourceDetailedCounts",
						),
						metadataKeys: this.preserveArrayValue(
							workingFilters.segmentVector?.metadataKeys,
							"metadataKeys",
						),
						allowedCountries: this.preserveArrayValue(
							workingFilters.segmentVector?.allowedCountries,
							"allowedCountries",
						),
						sourceBqTables: this.preserveArrayValue(
							workingFilters.segmentVector?.sourceBqTables,
							"sourceBqTables",
						),
						path: this.preserveArrayValue(
							workingFilters.segmentVector?.path,
							"path",
						),

						// Preserve boolean fields safely
						isAbm: workingFilters.segmentVector?.isAbm || false,
						// API expects List<String>, not boolean
						isOptimizedFor: Array.isArray(workingFilters.segmentVector?.isOptimizedFor)
							? workingFilters.segmentVector.isOptimizedFor
							: [],
						isLeaf:
							workingFilters.segmentVector?.isLeaf !== undefined
								? workingFilters.segmentVector.isLeaf
								: true,
						isNavOnly: workingFilters.segmentVector?.isNavOnly || false,
						isArchived: workingFilters.segmentVector?.isArchived || false,
						sellable: workingFilters.segmentVector?.sellable ,
						hasMeta:
							workingFilters.segmentVector?.hasMeta !== undefined
								? workingFilters.segmentVector.hasMeta
								: true,

						// Use helper functions for bulletproof numeric preservation
						numericMin: this.preserveNumericValue(
							workingFilters.segmentVector?.numericMin,
							"numericMin",
							0,
						),
						numericMax: this.preserveNumericValue(
							workingFilters.segmentVector?.numericMax,
							"numericMax",
							0,
						),
						reach: this.preserveNumericValue(
							workingFilters.segmentVector?.reach,
							"segmentVector.reach",
							0,
						),
						sourceProvidedCount: this.preserveNumericValue(
							workingFilters.segmentVector?.sourceProvidedCount,
							"sourceProvidedCount",
							0,
						),
						sourceMatchedCount: this.preserveNumericValue(
							workingFilters.segmentVector?.sourceMatchedCount,
							"sourceMatchedCount",
							0,
						),
						priceCpm: this.preserveNumericValue(
							workingFilters.segmentVector?.priceCpm,
							"priceCpm",
							0,
						),
						pricePom: this.preserveNumericValue(
							workingFilters.segmentVector?.pricePom,
							"pricePom",
							0,
						),
					},
					segmentMetrics: {
						// Use helper functions for array preservation
						segmentName: this.preserveArrayValue(
							workingFilters.segmentMetrics?.segmentName,
							"segmentMetrics.segmentName",
						),
						segmentExternalId: this.preserveArrayValue(
							workingFilters.segmentMetrics?.segmentExternalId,
							"segmentExternalId",
						),
						status: this.preserveArrayValue(
							workingFilters.segmentMetrics?.status,
							"segmentMetrics.status",
						),

						// Use helper functions for critical numeric filter preservation
						reach: this.preserveNumericValue(
							workingFilters.segmentMetrics?.reach,
							"segmentMetrics.reach",
							0,
						),
						avgCpm: this.preserveNumericValue(
							workingFilters.segmentMetrics?.avgEcpm,
							"segmentMetrics.avgEcpm",
							0,
						),
						requests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.requests,
							"requests",
							0,
						),
						soldImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.soldImpressions,
							"soldImpressions",
							0,
						),
						connectedTvDeviceIdRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.connectedTvDeviceIdRequests,
							"connectedTvDeviceIdRequests",
							0,
						),
						connectedTvDeviceIdSoldImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.connectedTvDeviceIdSoldImpressions,
							"connectedTvDeviceIdSoldImpressions",
							0,
						),
						uniqueUsers: this.preserveNumericValue(
							workingFilters.segmentMetrics?.uniqueUsers,
							"uniqueUsers",
							0,
						),
						uniqueUsersWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.uniqueUsersWithRequests,
							"uniqueUsersWithRequests",
							0,
						),
						uniqueUsersWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.uniqueUsersWithImpressions,
							"uniqueUsersWithImpressions",
							0,
						),
						uniqueConnectedTvDeviceIds: this.preserveNumericValue(
							workingFilters.segmentMetrics?.uniqueConnectedTvDeviceIds,
							"uniqueConnectedTvDeviceIds",
							0,
						),
						uniqueConnectedTvDeviceIdsWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics
								?.uniqueConnectedTvDeviceIdsWithRequests,
							"uniqueConnectedTvDeviceIdsWithRequests",
							0,
						),
						uniqueConnectedTvDeviceIdsWithImpressions:
							this.preserveNumericValue(
								workingFilters.segmentMetrics
									?.uniqueConnectedTvDeviceIdsWithImpressions,
								"uniqueConnectedTvDeviceIdsWithImpressions",
								0,
							),
						uniqueUsersViaConnectedTv: this.preserveNumericValue(
							workingFilters.segmentMetrics?.uniqueUsersViaConnectedTv,
							"uniqueUsersViaConnectedTv",
							0,
						),
						uniqueUsersViaConnectedTvWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics
								?.uniqueUsersViaConnectedTvWithRequests,
							"uniqueUsersViaConnectedTvWithRequests",
							0,
						),
						uniqueUsersViaConnectedTvWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics
								?.uniqueUsersViaConnectedTvWithImpressions,
							"uniqueUsersViaConnectedTvWithImpressions",
							0,
						),
						uniqueIpsWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.uniqueIpsWithRequests,
							"uniqueIpsWithRequests",
							0,
						),
						ipRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.ipRequests,
							"ipRequests",
							0,
						),
						cookies: this.preserveNumericValue(
							workingFilters.segmentMetrics?.cookies,
							"cookies",
							0,
						),
						cookiesWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.cookiesWithRequests,
							"cookiesWithRequests",
							0,
						),
						cookiesWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.cookiesWithImpressions,
							"cookiesWithImpressions",
							0,
						),
						mobileDevices: this.preserveNumericValue(
							workingFilters.segmentMetrics?.mobileDevices,
							"mobileDevices",
							0,
						),
						mobileDevicesWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.mobileDevicesWithRequests,
							"mobileDevicesWithRequests",
							0,
						),
						mobileDevicesWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.mobileDevicesWithImpressions,
							"mobileDevicesWithImpressions",
							0,
						),
						tabletDevices: this.preserveNumericValue(
							workingFilters.segmentMetrics?.tabletDevices,
							"tabletDevices",
							0,
						),
						tabletDevicesWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.tabletDevicesWithRequests,
							"tabletDevicesWithRequests",
							0,
						),
						tabletDevicesWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.tabletDevicesWithImpressions,
							"tabletDevicesWithImpressions",
							0,
						),
						connectedTvDevices: this.preserveNumericValue(
							workingFilters.segmentMetrics?.connectedTvDevices,
							"connectedTvDevices",
							0,
						),
						connectedTvDevicesWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.connectedTvDevicesWithRequests,
							"connectedTvDevicesWithRequests",
							0,
						),
						connectedTvDevicesWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.connectedTvDevicesWithImpressions,
							"connectedTvDevicesWithImpressions",
							0,
						),
						households: this.preserveNumericValue(
							workingFilters.segmentMetrics?.households,
							"households",
							0,
						),
						householdsWithRequests: this.preserveNumericValue(
							workingFilters.segmentMetrics?.householdsWithRequests,
							"householdsWithRequests",
							0,
						),
						householdsWithImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.householdsWithImpressions,
							"householdsWithImpressions",
							0,
						),
						advertiserSpend: this.preserveNumericValue(
							workingFilters.segmentMetrics?.advertiserSpend,
							"advertiserSpend",
							0,
						),
						connectedTvDeviceIdAdvertiserSpend: this.preserveNumericValue(
							workingFilters.segmentMetrics?.connectedTvDeviceIdAdvertiserSpend,
							"connectedTvDeviceIdAdvertiserSpend",
							0,
						),
						avgSpendPerUniqueUser: this.preserveNumericValue(
							workingFilters.segmentMetrics?.avgSpendPerUniqueUser,
							"avgSpendPerUniqueUser",
							0,
						),
						stddevCpm: this.preserveNumericValue(
							workingFilters.segmentMetrics?.stddevEcpm,
							"stddevEcpm",
							0,
						),
						minCpm: this.preserveNumericValue(
							workingFilters.segmentMetrics?.minEcpm,
							"minEcpm",
							0,
						),
						maxCpm: this.preserveNumericValue(
							workingFilters.segmentMetrics?.maxEcpm,
							"maxEcpm",
							0,
						),
						impressionFrequency: this.preserveNumericValue(
							workingFilters.segmentMetrics?.impressionFrequency,
							"impressionFrequency",
							0,
						),
						impressionFrequencyPerDay: this.preserveNumericValue(
							workingFilters.segmentMetrics?.impressionFrequencyPerDay,
							"impressionFrequencyPerDay",
							0,
						),
						weightedClickThroughRate: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedClickThroughRate,
							"weightedClickThroughRate",
							0,
						),
						weightedRevenuePerImpression: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedRevenuePerImpression,
							"weightedRevenuePerImpression",
							0,
						),
						weightedVideoThroughRate: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedVideoThroughRate,
							"weightedVideoThroughRate",
							0,
						),
						weightedViewability: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedViewability,
							"weightedViewability",
							0,
						),
						weightedOxWinRate: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedOxWinRate,
							"weightedOxWinRate",
							0,
						),
						weightedPubWinRate: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedPubWinRate,
							"weightedPubWinRate",
							0,
						),
						weightedBidCpm:
							this.preserveNumericValue(
								workingFilters.segmentMetrics?.weightedBidCpm,
								"weightedBidCpm",
								0,
							) * 1000000,
						weightedBidGuidance: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedBidGuidance,
							"weightedBidGuidance",
							0,
						),
						weightedImpressions: this.preserveNumericValue(
							workingFilters.segmentMetrics?.weightedImpressions,
							"weightedImpressions",
							0,
						),
					},
					demographicFilters: {
						ageRange: (() => {
							const r = workingFilters.demographicFilters?.ageRange;
							// API expects Integer (nullable) — never send "" strings
							const start = r?.start !== undefined && r.start !== "" && r.start !== null
								? parseInt(r.start, 10) : null;
							const end = r?.end !== undefined && r.end !== "" && r.end !== null
								? parseInt(r.end, 10) : null;
							// Only include if we have valid integers; otherwise omit with nulls
							return {
								start: !isNaN(start) ? start : null,
								end: !isNaN(end) ? end : null,
							};
						})(),
						location: Array.isArray(workingFilters.demographicFilters?.location)
							? workingFilters.demographicFilters.location
							: [],
						locationType: (() => {
							const lt = workingFilters.demographicFilters?.locationType;
							// Accept only known API values; fall back to null otherwise
							const valid = ["STATE", "DMA", "CONGRESSIONAL",
							               "state", "dma", "congressional"];
							return valid.includes(lt) ? lt : null;
						})(),
						gender: workingFilters.demographicFilters?.gender || null,
					},
					categoryFilters: processedCategoryFilters,
					cumulativeDataFilters: {
						cumulativeSetsFiltersMap: {
							"Unique User Optimised": {
								sortBy: "score",
								isASC: true,
								filterBy: "uniqueUsers",
								threshold:
									workingFilters.cumulativeThresholds?.uniqueUsers ||
									1500000000,
							},
							"Composite Score Optimised": {
								sortBy: "compositeScore",
								isASC: false,
								filterBy: "calculatedReach",
								threshold:
									workingFilters.cumulativeThresholds?.reach_composite ||
									38000000,
							},
							"Relevance Optimised": {
								sortBy: "score",
								isASC: true,
								filterBy: "calculatedReach",
								threshold:
									workingFilters.cumulativeThresholds?.reach_relevance ||
									38000000,
							},
							"Impressions Optimised": {
								sortBy: "score",
								isASC: true,
								filterBy: "weightedImpressions",
								threshold:
									workingFilters.cumulativeThresholds?.weightedImpressions ||
									700000,
							},
						},
					},
				},
				sortBy: options.sortBy || workingFilters.sortBy || "compositeScore",
				sortDirection: options.view || workingFilters.view || "DESC",
				startIndex: options.startIndex || workingFilters.startIndex || 0,
				limit: options.noOfResult || workingFilters.noOfResult || 10,
				lookbackWindowInDays: options.noOfDays || workingFilters.noOfDays || 0,
				includeAudience:
					options.isAudienceRequire !== undefined
						? options.isAudienceRequire
						: workingFilters.isAudienceRequire !== undefined
							? workingFilters.isAudienceRequire
							: false,
			};

			console.log(
				`🔧 CORRECTED [${requestId}]: Final Payload Structure with preserved filters:`,
			);
			console.log("  📤 query:", searchPayload.query);
			console.log("  📤 excludeQuery:", searchPayload.excludeQuery);
			console.log("  📤 sortBy:", searchPayload.sortBy);
			console.log("  📤 sortDirection:", searchPayload.sortDirection);
			console.log("  📤 limit:", searchPayload.limit);
			console.log(
				"  📤 filters.segmentVector.audienceType:",
				searchPayload.filters.segmentVector.audienceType,
			);
			console.log(
				"  📤 filters.segmentVector.categoryL1:",
				searchPayload.filters.segmentVector.categoryL1,
			);
			console.log(
				"  📤 filters.segmentVector.source:",
				searchPayload.filters.segmentVector.source,
			);
			console.log(
				"  📤 filters.segmentMetrics.reach:",
				searchPayload.filters.segmentMetrics.reach,
			);
			console.log(
				"  📤 filters.segmentMetrics.avgCpm:",
				searchPayload.filters.segmentMetrics.avgCpm,
			);

			const response = await this.searchSegments(searchPayload, options.signal);
			return response;
		} catch (error) {
			console.error(
				`❌ CORRECTED [${requestId}]: performSegmentSearch failed:`,
				error,
			);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	async searchSegments(searchPayload, signal = null) {
		try {
			console.log("🔧 CORRECTED: Making API call with verified payload");

			const response = await this.makeRequest("/segment/search", {
				method: "POST",
				body: JSON.stringify(searchPayload),
				signal,
			});

			return response;
		} catch (error) {
			console.error("❌ CORRECTED: Segments search failed:", error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	clearFilterCache() {
		this.filtersCache.clear();
		console.log("✅ Segments filter cache cleared");
	}

	async getPrebuiltSets() {
		try {
			const mockSets = {
				relevance: {
					id: "relevance",
					name: "Relevance",
					description: "Segments sorted by reach with minimum threshold",
					segments: [],
					filters: {
						sortBy: "reach",
						minReach: 1000,
					},
				},
				performance: {
					id: "performance",
					name: "Performance",
					description: "High-performing segments with strong eCPM",
					segments: [],
					filters: {
						sortBy: "avgEcpm",
						minEcpm: 5.0,
					},
				},
				"cost-optimized": {
					id: "cost-optimized",
					name: "Cost Optimized",
					description: "Budget-friendly segments with good reach",
					segments: [],
					filters: {
						sortBy: "reach",
						maxEcpm: 3.0,
					},
				},
			};

			return {
				success: true,
				data: mockSets,
			};
		} catch (error) {
			console.error("Get prebuilt sets failed:", error);
			return {
				success: false,
				error: error.message,
			};
		}
	}
}

const segmentsApiService = new SegmentsApiService();
window.segmentsApiService = segmentsApiService;

console.log("🔧 FIXED SegmentsApiService loaded with redirect loop prevention");

export default segmentsApiService;
